import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck } from "react-icons/fa";
import { FiEdit} from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import TableSkeleton from "../../../TableSkeleton";
import AddPathologyTest from "./AddPathologyTest";
import { saveToCache, readFromCache } from "../../../../services/pathologyCache";
import { getPendingTests } from "../../../../services/pathologyOutbox";
import { onSyncStatusChange } from "../../../../services/pathologySyncEngine";
import AccountExportOptions from "../../../AccountExportOptions";
import Pagination from "../../../Shared/Pagination";

const API_URL = "https://api.muktihospital.com/api/pathology";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PathologyTestModal = ({ isOpen, onClose, onSuccess, editing }) => {
  if (!isOpen) return null;

  return (
    <AddPathologyTest
      initialData={editing}
      onSuccess={onSuccess}
      onCancel={onClose}
    />
  );
};

const PathologyManager = () => {
  const [pathology, setPathology] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 20;

  const fetchPathology = useCallback(async () => {
    setLoading(true);

    // Helper: map a pending outbox test item to a row the UI can render.
    const mapPendingTestToRow = (item) => {
      const p = item.payload || {};
      return {
        id: `offline-${item.clientId}`,
        testName: p.testName,
        testCode: p.testCode,
        categoryName: p.categoryName,
        tax: p.tax,
        standardCharge: p.standardCharge,
        amount: p.amount,
        commission: p.commission,
        commissionType: p.commissionType,
        commissionValue: p.commissionValue,
        expense: p.expense,
        __offline: true, // marker for pending-sync styling / action gating
      };
    };

    // Pending (offline-created) tests not yet synced — always shown on top.
    let pendingRows = [];
    try {
      const pending = await getPendingTests();
      pendingRows = pending.map(mapPendingTestToRow);
    } catch (e) {
      pendingRows = [];
    }

    try {
      const res = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
      });
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];
      const validList = list.filter(
        (item) => item && typeof item === "object" && item.id
      );
      const sortedList = validList.sort((a, b) => b.id - a.id);
      const merged = [...pendingRows, ...sortedList];
      setPathology(merged);
      setFiltered(merged);
      setTotalRecords(merged.length);
      // Cache the SERVER list only (not the pending rows).
      saveToCache("pathology_tests", sortedList);
    } catch (err) {
      console.error("Fetch error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      // Offline / server down: fall back to cached list + pending rows.
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = (await readFromCache("pathology_tests")) || [];
        const merged = [...pendingRows, ...cached];
        setPathology(merged);
        setFiltered(merged);
        setTotalRecords(merged.length);
        if (merged.length > 0) {
          toast.info("Offline: showing cached tests.");
        }
      } else {
        // Still show pending rows even if server failed for non-network reasons.
        setPathology(pendingRows);
        setFiltered(pendingRows);
        setTotalRecords(pendingRows.length);
        toast.error(err.response?.data?.error || "Failed to load tests.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPathology();
  }, [fetchPathology]);

  // Auto-refresh the test list when the sync engine finishes pushing
  // pending offline items (bills + tests). Mirrors the bill list behaviour.
  useEffect(() => {
    const unsub = onSyncStatusChange((status) => {
      if (status && status.justSynced && status.justSynced > 0) {
        // Small delay so the server commit settles before we re-fetch.
        setTimeout(() => {
          fetchPathology();
        }, 1200);
      }
    });
    return () => unsub();
  }, [fetchPathology]);

  useEffect(() => {
    const term = search.trim().toLowerCase();
    const result = pathology.filter(
      (item) =>
        item &&
        typeof item === "object" &&
        item.id &&
        (
          item.testName?.toLowerCase().includes(term) ||
          item.testCode?.toLowerCase().includes(term)
        )
    );
    setFiltered(result);
    setCurrentPage(1);
  }, [search, pathology]);

  const filteredList = filtered
    .filter((item) => {
      const isValid = item && typeof item === "object" && item.id;
      if (!isValid) {
        console.warn("Invalid item in filteredList:", item);
      }
      return isValid;
    })
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredList.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (testId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(testId)
        ? prevSelectedRows.filter((id) => id !== testId)
        : [...prevSelectedRows, testId]
    );
  };

  const handleDelete = async (ids) => {
    try {
      if (Array.isArray(ids)) {
        await Promise.all(
          ids.map((id) =>
            axios.delete(`${API_URL}/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
        toast.success(`${ids.length} test(s) deleted successfully.`);
      } else {
        await axios.delete(`${API_URL}/${ids}`, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Test deleted successfully.");
      }
      setSelectedRows([]);
      fetchPathology();
    } catch (err) {
      console.error("Delete error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to delete test(s).");
    }
  };

  const openModal = (data = null) => {
    setEditing(data);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSuccess = (newTest) => {
    if (!newTest || !newTest.id || typeof newTest !== "object") {
      console.error("Invalid test data received:", newTest, {
        source: "AddPathologyTest",
        editing: !!editing,
        state: { pathologyLength: pathology.length, filteredLength: filtered.length },
      });
      toast.error("Failed to process test data. Refreshing data...");
      fetchPathology();
      closeModal();
      return;
    }
    if (editing) {
      setPathology((prev) =>
        prev.map((test) => (test.id === newTest.id ? newTest : test))
      );
      setFiltered((prev) =>
        prev.map((test) => (test.id === newTest.id ? newTest : test))
      );
    } else {
      setPathology((prev) => [newTest, ...prev]);
      setFiltered((prev) => [newTest, ...prev]);
      setTotalRecords((prev) => prev + 1);
      setCurrentPage(1);
    }
    closeModal();
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "No.",
    "Test Code",
    "Test Name",
    "Category",
    "Amount",
    "Comm. Value",
    "Commission (BDT)"
  ];
  const dataMapper = (item, index, array) => {
    const serial = array.indexOf(item) + 1;
    return [
      serial,
      item.testCode || "N/A",
      item.testName || "",
      item.categoryName || "",
      item.amount || 0,
      item.commissionValue || 0,
      item.commission || 0,
    ].join("\t");
  };

  const testSummaryText = `Total Active Pathology Tests: ${filtered.length}`;

  const skeletonColumns = [
    { width: 20, height: 16, className: "print-hidden-in-print" },
    { width: 30, height: 16 },
    { width: 60, height: 16 },
    { width: 150, height: 16 },
    { width: 100, height: 16 },
    { width: 80, height: 16 },
    { width: 60, height: 16 },
    { width: 100, height: 16 },
    { width: 80, height: 16 },
    { width: 80, height: 16 },
    { width: 80, height: 16, className: "print-hidden-in-print" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Pathology Tests
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-56 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal()}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Test
              </button>
              <AccountExportOptions
                  data={filtered}
                  title="Pathology Test Database Report"
                  subTitle={testSummaryText}
                  headers={headers}
                  dataMapper={(item, index) => dataMapper(item, index, filtered)}
                  dropdownOpen={dropdownOpen}
                  toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
                />
            </div>
          </div>
        </div>
        <div>
          <style>
            {`
              @media print {
                .print-hidden, .print-hidden-in-print {
                  display: none !important;
                }
                .print-all-rows {
                  display: table-row !important;
                }
                .print-table {
                  width: 100%;
                  border-collapse: collapse;
                }
                .print-table th, .print-table td {
                  border: 1px solid #000;
                  padding: 8px;
                  text-align: left;
                  font-size: 12px;
                }
                .print-table th {
                  background-color: #f0f0f0;
                  font-weight: bold;
                }
                .print-table tbody tr:nth-child(even) {
                  background-color: #f9f9f9;
                }
              }
            `}
          </style>
          <div className="overflow-x-auto print-wrapper">
            <table className="min-w-[1000px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          filteredList.length > 0 &&
                          filteredList.every(
                            (item) => item && item.id && selectedRows.includes(item.id)
                          )
                        }
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    No.
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Test Code</th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Test Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Category
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Standard Charge
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Amount
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Comm. Type</th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Comm. Value</th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Commission (৳)
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : filteredList.length === 0 ? (
                  <tr className="print-hidden">
                    <td
                      colSpan={9}
                      className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                    >
                      No tests found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {filteredList.map((item, i) => {
                      const serialNumber =
                        (currentPage - 1) * itemsPerPage + i + 1;
                      return (
                        <tr
                          key={`normal-${item.id}`}
                          className="border-b hover:bg-gray-50 print-hidden"
                        >
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[3px]">
                              <input
                                type="checkbox"
                                id={`test-${i}`}
                                className="hidden peer print-hidden"
                                checked={selectedRows.includes(item.id)}
                                onChange={() => handleRowSelect(item.id)}
                              />
                              <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                              </span>
                              <label
                                htmlFor={`test-${i}`}
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                              ></label>
                            </div>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {serialNumber}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{item.testCode || "N/A"}</td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.testName || "N/A"}
                            {item.__offline && (
                              <span className="ml-2 inline-block bg-orange-50 text-orange-600 border border-orange-200 text-[10px] font-medium px-2 py-0.5 rounded-full align-middle">
                                Pending Sync
                              </span>
                            )}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.categoryName || "N/A"}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.standardCharge || 0}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.amount || 0}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            <span className={`px-2 py-0.5 rounded-full font-medium ${item.commissionType === 'percentage' ? 'bg-purple-50 text-purple-600 border border-purple-200' : 'bg-orange-50 text-orange-600 border border-orange-200'}`}>
                              {item.commissionType === 'percentage' ? 'Percentage' : 'Fixed'}
                            </span>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.commissionValue || 0} {item.commissionType === 'percentage' ? '%' : '৳'}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {item.commission || 0}
                          </td>
                          <td className="p-3 text-left space-x-2 print-hidden-in-print flex shrink-0">
                            <button
                              onClick={() => openModal(item)}
                              className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                              title="Edit"
                            >
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedRows([item.id]);
                                setDeleteModalOpen(true);
                              }}
                              className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                              title="Delete"
                            >
                              <RiDeleteBin6Line />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {Array.isArray(filtered) && filtered.length > 0 ? (
                      filtered
                        .filter((item) => item && typeof item === "object" && item.id)
                        .map((item, i) => {
                          const serialNumber = i + 1;
                          return (
                            <tr
                              key={`print-${item.id}`}
                              className="border-b print-all-rows"
                              style={{ display: "none" }}
                            >
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 print-hidden-in-print"></td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {serialNumber}
                              </td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{item.testCode || "N/A"}</td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {item.testName || "N/A"}
                              </td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {item.categoryName || "N/A"}
                              </td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {item.standardCharge || 0}
                              </td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {item.amount || 0}
                              </td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{item.commissionType || "fixed"}</td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">{item.commissionValue || 0}</td>
                              <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                                {item.commission || 0}
                              </td>
                              <td className="p-3 text-left space-x-2 print-hidden-in-print"></td>
                            </tr>
                          );
                        })
                    ) : (
                      <tr
                        className="print-all-rows"
                        style={{ display: "none" }}
                      >
                        <td
                          colSpan={8}
                          className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
                        >
                          No tests found.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Reusable Pagination */}
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={filtered.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <PathologyTestModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSuccess={handleSuccess}
        editing={editing}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="test"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          selectedRows.length === 1
            ? "Do you really want to delete this test?"
            : `Do you really want to delete ${selectedRows.length} tests?`
        }
      />
    </div>
  );
};

export default PathologyManager;

